import { useEffect, useRef, useState } from "react";
import { getHouseCompetitionWinners } from "./house-service";
import { HouseWinnersData } from "./types";
import { Navigation, A11y, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/a11y";
import Card from "../../../components/Card/card";
import { Winner } from "../../winners/types";
import Skeleton from "react-loading-skeleton";
import DOMPurify from "dompurify";

const HouseWinners = () => {
  const [winners, setWinners] = useState<HouseWinnersData>();
  const [maxWidth, setMaxWidth] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const swiperContainerDivRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    getHouseWinnersData();
  }, []);

  useEffect(() => {
    if (swiperContainerDivRef.current) {
      setMaxWidth(swiperContainerDivRef.current.clientWidth);
    }
  }, [winners, swiperContainerDivRef.current?.clientWidth]);

  const getHouseWinnersData = async () => {
    try {
      const response = await getHouseCompetitionWinners();
      const data = (await response.json()) as HouseWinnersData;
      setWinners(data);
    } catch (error) {
      console.error("Error fetching house winners data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getWinStartDate = (winStartDate: string) => {
    const day = new Date(winStartDate).getDate();
    const month = new Date(winStartDate);
    const monthName = month.toLocaleString("en-us", { month: "short" });

    return `${day < 10 ? `0${day}` : day} ${monthName}`;
  };

  const getHeaderData = (winner: Winner) => (
    <div className="bg-[#D1AC05] relative flex p-1 justify-center items-center rounded-t-xl lg:h-[46px] xs:h-[38px]">
      <img
        src="/assets/images/house-competition/won-image.png"
        alt="won-image"
        className="absolute self-center left-0 z-[2]"
        style={{
          width: "80px",
          height: "80px",
        }}
        loading="lazy"
      />
      <div
        className="absolute left-1/2 transform -translate-x-1/2 flex items-center overflow-hidden"
        style={{
          width: "calc(100% - 170px)",
        }}>
        <div className="overflow-hidden w-[99px] border-r-2 border-solid border-r-[#00000014]">
          <div className="text-white font-jost font-medium lg:text-[12px] xs:text-[10px] lg:leading-4 xs:leading-3 truncate w-full uppercase">
            {winner.CompetitionName}
          </div>
          <div className="text-white font-jost font-semibold lg:text-[16px] xs:text-[14px] lg:leading-[22px] xs:leading-5 truncate w-full">
            {winner.Prize}
          </div>
        </div>
        <div className="overflow-hidden w-[81px] pl-[13px]">
          <div className="text-white uppercase font-jost font-medium lg:text-[12px] xs:text-[10px] lg:leading-4 xs:leading-3 truncate w-full">
            Won For
          </div>
          <div className="text-white font-jost font-semibold lg:text-[16px] xs:text-[14px] lg:leading-[22px] xs:leading-5 truncate w-full">
            {winner.TicketPriceWon}
          </div>
        </div>
      </div>
      <div
        className="bg-black absolute top-0 right-0 flex flex-col h-full justify-center p-4"
        style={{ borderTopRightRadius: "12px" }}
        id="rightPositionedDiv">
        <span className="uppercase text-white font-jost font-semibold lg:text-[12px] xs:text-[10px] lg:leading-4 xs:leading-3">
          {getWinStartDate(winner.StartDate)}
        </span>
        <span className="text-white font-jost font-semibold lg:text-[16px] xs:text-[14px] lg:leading-[22px] xs:leading-5">
          {winner.Date.Year}
        </span>
      </div>
    </div>
  );

  const getBodyData = (winner: Winner) => (
    <div className="relative lg:h-[214px] xs:h-[172px]">
      {winner.PrizeImages != null && winner.PrizeImages.length > 0 && (
        <img className="w-full h-full relative z-[1]" src={winner.PrizeImages[0]} loading="lazy" />
      )}
      <img className="winner-ribbon" src="/assets/images/prize-details/winner-ribbon.png" loading="lazy" />
      <img className="winner-photo" src={winner.Photo} alt={winner.Name} loading="lazy" />
      <span className="winner-name font-jost font-normal text-[12px]">{winner.Name}</span>
    </div>
  );

  const getFooterData = (winner: Winner) => (
    <div className="bg-white py-12 px-4 font-jost font-normal lg:text-[16px] text-[#696971] text-center overflow-hidden rounded-b-xl lg:h-[190px] xs:h-[195px] xs:text-[14px] lg:leading-[22px] xs:leading-5">
      <p
        className="lg:h-[88px] xs:h-[80px]"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 4, // limits text to 4 lines
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(winner.Testimonial),
        }}
      />
    </div>
  );

  return isLoading ? (
    <Skeleton inline highlightColor="#fff" height={500} containerClassName="w-full bg-[#f1f1f1]" />
  ) : (
    winners?.HouseWinners?.winnerList?.length > 0 && (
      <div
        className="lg:rounded-lg flex flex-col items-center relative"
        style={{
          background: "linear-gradient(32deg, #6BE7FF 1.01%, #C1FFEC 92.22%)",
        }}
        ref={swiperContainerDivRef}>
        <div className="flex items-center mt-4">
          <img src="/assets/images/house-competition/winners-left-leaf.png" alt="winners-left-leaf" loading="lazy" />
          <h5 className="lg:text-[28px] xs:text-[20px] font-jost text-[#28282B] font-semibold text-nowrap">
            Meet Our House Winners
          </h5>
          <img src="/assets/images/house-competition/winners-right-leaf.png" alt="winners-right-leaf" loading="lazy" />
        </div>
        <div
          className="w-full carousel-container-with-top-navigation lg:mt-5 xs:px-2 lg:px-12 pb-4"
          style={{ maxWidth }}>
          <Swiper
            modules={[Navigation, A11y, Pagination]}
            pagination={{ clickable: true }}
            className="xs:!pt-0 lg:!pt-[2rem] !pb-[2rem]"
            navigation
            breakpoints={{
              1080: {
                slidesPerView: 2,
                spaceBetween: 16,
              },
              204: {
                slidesPerView: 1.2,
                spaceBetween: 10,
              },
            }}>
            {winners?.HouseWinners?.winnerList.map((winner, index) => (
              <SwiperSlide key={index} className="xs:!w-[320px] lg:!w-[340px]">
                <Card
                  header={getHeaderData(winner)}
                  body={getBodyData(winner)}
                  footer={getFooterData(winner)}
                  containerClassName="flex-col w-full flex lg:h-[470px] xs:h-[425px] relative pt-[20px]"
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    )
  );
};

export default HouseWinners;
